import { Dialog } from "@progress/kendo-react-dialogs";
import { Splitter, type SplitterOnChangeEvent, type SplitterPaneProps } from "@progress/kendo-react-layout";
import { PDFExport } from "@progress/kendo-react-pdf";
import React, { useCallback, useRef, useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import ChatConversation from "../../../chat/chatConversation";
import CommercialHistoryPanel from "../../../chat/commercialHistoryPanel";
import { useIntegerQueryParam } from "../../../hooks/query/useQueryParam";
import useSimulationReadiness from "../../../hooks/useSimulationReadiness";
import { selectCurrentCommercialPlan } from "../../../slices/chatSlice";
import CommercialPlanPanel from "./components/commercialPlanPanel";
import SimulationHeading from "./components/simulationHeading";
import SimulationInputs from "./components/simulatorInputs";
import SimulationReadiness from "./components/simulationReadiness";
import SimulationToolbar from "./components/simulatorToolbar";

const Simulator: React.FC = () => {
    const currentPlan = useAppSelector(selectCurrentCommercialPlan);
    const { showReadinessPanel } = useSimulationReadiness();

    const [showSimulatorInput, setShowSimulatorInput] = useState(false);
    const [isCommercialHistoryOpen, setIsCommercialHistoryOpen] = useState(false);
    const commercialPlanPdfExportRef = useRef<PDFExport | null>(null);

    const [selectedConversationId, setSelectedConversationId] = useIntegerQueryParam("id");

    const [panes, setPanes] = useState<SplitterPaneProps[]>([
        {
            size: "35%",
            min: "240px",
            collapsible: true
        },
        {
            min: "320px",
            collapsible: false
        }
    ]);

    const handleChange = (event: SplitterOnChangeEvent): void => {
        setPanes(event.newState);
    };

    const handleConversationChanged = useCallback((conversationId: number): void => {
        setSelectedConversationId(conversationId, { replace: false });
    }, [setSelectedConversationId]);

    const handleExportCommercialPlan = useCallback((): void => {
        commercialPlanPdfExportRef.current?.save();
    }, []);

    return (
        <>
            <div className="d-flex h-100 w-100 min-h-0">
                <CommercialHistoryPanel
                    isOpen={isCommercialHistoryOpen}
                    onChange={handleConversationChanged}
                    onClose={() => setIsCommercialHistoryOpen(false)}
                />

                <div className="flex-grow-1 min-w-0 min-h-0">
                    <Splitter
                        style={{ height: "100%", width: "100%" }}
                        panes={panes}
                        onChange={handleChange}
                    >
                        <div className="h-100 w-100 min-w-0 overflow-hidden d-flex flex-column">
                            <ChatConversation
                                conversationId={selectedConversationId}
                                onConversationChanged={setSelectedConversationId}
                                isCommercialHistoryOpen={isCommercialHistoryOpen}
                                onToggleCommercialHistory={() => setIsCommercialHistoryOpen(current => !current)}
                            />
                        </div>

                        <div className={`h-100 w-100 min-w-0 overflow-x-hidden overflow-y-auto ${showReadinessPanel ? "bg-white" : ""}`}>
                            <div className="simulator-plan-pane container d-flex flex-column p-4 mx-auto min-h-100 min-w-0 w-100">
                                {showReadinessPanel
                                    ? (
                                            <div className="d-flex flex-column flex-grow-1 min-w-0 align-self-center" style={{ width: "725px", maxWidth: "100%" }}>
                                                <SimulationReadiness />
                                            </div>
                                        )
                                    : (
                                            <>
                                                <SimulationToolbar
                                                    onExport={handleExportCommercialPlan}
                                                    onShowSimulatorInput={() => setShowSimulatorInput(true)}
                                                />

                                                <div className="d-flex flex-column flex-grow-1 min-w-0 w-100 gap-4">
                                                    <CommercialPlanPanel />
                                                </div>
                                            </>
                                        )}
                            </div>
                        </div>
                    </Splitter>
                </div>
            </div>

            {currentPlan && (
                <div
                    aria-hidden="true"
                    style={{
                        left: -10000,
                        pointerEvents: "none",
                        position: "absolute",
                        top: 0,
                        width: 1400
                    }}
                >
                    <PDFExport
                        ref={commercialPlanPdfExportRef}
                        fileName="commercial-plan.pdf"
                        paperSize="A4"
                        landscape={true}
                        margin="0.4in"
                        scale={0.55}
                        repeatHeaders={true}
                    >
                        <div className="commercial-plan-pdf bg-white p-2">
                            <style>
                                /* Hides all the buttons */
                                {`.commercial-plan-pdf button, .commercial-plan-pdf .k-button { display: none !important; }`}
                            </style>
                            <SimulationHeading />
                            <CommercialPlanPanel />
                        </div>
                    </PDFExport>
                </div>
            )}

            {showSimulatorInput && (
                <Dialog title="Simulation Inputs" width={700} onClose={() => setShowSimulatorInput(false)}>
                    <SimulationInputs />
                </Dialog>
            )}
        </>
    );
};

export default Simulator;
